import { PartInfoResponse, useAvailabilityCache } from './availability-cache-provider';
import { useState, useCallback, useRef, useEffect } from 'react';

type FetchParams = {
  partNo?: string;
  fullPartNo?: string;
  soHeaderId?: string;
};

export const usePartInfo = (
  params: FetchParams,
  mainIndex: number,
  rowIndex: number,
  isShow: boolean,
  onSuccess?: (resp: PartInfoResponse) => void
) => {
  const [data, setData] = useState<PartInfoResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hasFetched = useRef(false);
  const { cache, needReload, setNeedReload, mainIndex: _mainIndex, rowIndex: _rowIndex } = useAvailabilityCache();

  const fetchPartInfo = async (_params: FetchParams, useCache = true) => {
    if (!_params?.partNo) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setData(null);
    const cacheKey = `${_params.partNo || ''}-${_params.fullPartNo || ''}-${_params.soHeaderId || ''}`;

    try {
      if (useCache && cache.has(cacheKey)) {
        setData(cache.get(cacheKey)!);
        setLoading(false);
        return;
      }

      const baseUrl = 'https://jobapi.advance-motors.co.uk/WebScraping/GetPartInfoFromServiceBox';
      const query = new URLSearchParams();

      if (_params.partNo) query.append('partNo', _params.partNo?.replace(/^0+(?!$)/, ''));
      if (_params.fullPartNo) query.append('fullPartNo', _params.fullPartNo);
      if (_params.soHeaderId) query.append('soHeaderId', _params.soHeaderId);

      const url = `${baseUrl}?${query.toString()}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result: PartInfoResponse = await response.json();

      cache.set(cacheKey, result);
      setData(result);
      onSuccess?.(result);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isShow) return;

    if (needReload && _mainIndex == mainIndex && _rowIndex == rowIndex) {
      fetchPartInfo(params, false);
      setNeedReload(false);
    }
  }, [needReload]);

  return { data, loading, error, fetchPartInfo };
};
